import { useState } from "react";
import { Send, Eye, AlertTriangle, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAdminAuth } from "@/lib/AdminAuthContext";
import { adminFetchJson } from "@/lib/adminFetch";
import AdminLayout from "./AdminLayout";
import { toast } from "sonner";

const SEGMENTS = [
  { id: "newsletter", label: "Suscriptores Newsletter", hint: "Lectores del blog que dejaron su correo" },
  { id: "leads_all", label: "Todos los Leads", hint: "Cualquiera que haya completado el Quiz" },
  { id: "leads_converted", label: "Leads convertidos", hint: "Solo clientes que ya compraron" },
  { id: "leads_pending", label: "Leads pendientes", hint: "Aún no compraron" },
];

const PRESETS = [
  {
    label: "Nuevo artículo en el blog",
    subject: "Hola {{name}}, hay algo nuevo que quiero contarte",
    body: `<p>Hola {{name}},</p>
<p>Acabo de publicar un nuevo artículo en el blog que creo te puede servir.</p>
<p><strong>👉 Lee aquí:</strong> <a href="https://elioralongevity.com/blog">https://elioralongevity.com/blog</a></p>
<p>Si quieres profundizar en lo que necesitas, mi <a href="https://elioralongevity.com/#quiz">Quiz Wellness</a> sigue gratis.</p>
<p>Un abrazo grande,<br/>Sarai</p>`,
  },
  {
    label: "Promo / oferta especial",
    subject: "{{name}}, esto solo dura unos días",
    body: `<p>Hola {{name}},</p>
<p>Tengo una oferta especial este mes para el <strong>Kit de Inicio Immunocal</strong>.</p>
<p>Si llevas tiempo considerándolo, este es el momento. Te escribo personalmente para que aproveches el descuento de afiliada.</p>
<p>📲 Escríbeme por WhatsApp y armamos tu pedido en 2 minutos.</p>
<p>Con cariño,<br/>Sarai</p>`,
  },
  {
    label: "Check-in personal",
    subject: "{{name}}, ¿cómo te ha ido?",
    body: `<p>Hola {{name}},</p>
<p>Hace tiempo que no platicamos y quería saber cómo te ha ido con tu protocolo.</p>
<p>¿Sentiste cambios en estas semanas? Cuéntame.</p>
<p>Si necesitas ajustar algo o tienes preguntas, contesta este correo o escríbeme por WhatsApp.</p>
<p>Aquí estoy,<br/>Sarai</p>`,
  },
];

export default function BroadcastComposer() {
  useAdminAuth();
  const [segment, setSegment] = useState("newsletter");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [dryRun, setDryRun] = useState(null);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const preview = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Completa el asunto y el cuerpo");
      return;
    }
    try {
      const data = await adminFetchJson("/api/admin/email/broadcast", {
        method: "POST",
        body: JSON.stringify({ segment, subject, body_html: body, dry_run: true }),
      });
      setDryRun(data);
    } catch (e) {
      toast.error(`Preview falló: ${e.message}`);
    }
  };

  const send = async () => {
    if (!dryRun || (dryRun.recipient_count || 0) === 0) {
      toast.error("Primero haz preview y verifica que hay destinatarios");
      return;
    }
    if (!window.confirm(`¿Enviar a ${dryRun.recipient_count} personas? Esta acción no se puede deshacer.`)) return;
    setSending(true);
    try {
      const data = await adminFetchJson("/api/admin/email/broadcast", {
        method: "POST",
        body: JSON.stringify({ segment, subject, body_html: body, dry_run: false }),
      });
      setLastResult(data);
      toast.success(`Enviados: ${data.sent} · Fallidos: ${data.failed_count}`);
    } catch (e) {
      toast.error(`Envío falló: ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  const applyPreset = (p) => {
    setSubject(p.subject);
    setBody(p.body);
    setDryRun(null);
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#bc6c25] font-semibold">Broadcast</p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#1a2421] mt-1">Email a un segmento</h1>
        <p className="text-sm text-[#5c6b61] mt-2 max-w-2xl">
          Envía un correo personalizado a un grupo específico (suscriptores del newsletter, leads, convertidos).
          Usa <code className="px-1.5 py-0.5 bg-[#f0ebe1] rounded text-xs">{"{{name}}"}</code> en asunto o cuerpo para personalizar.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          {/* Segmento */}
          <div className="rounded-2xl border border-[#2c4a3b]/10 bg-white p-5">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#5c6b61] font-semibold mb-3">1. Elige segmento</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {SEGMENTS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => { setSegment(s.id); setDryRun(null); }}
                  data-testid={`segment-${s.id}`}
                  className={`text-left p-3 rounded-xl border transition-all ${
                    segment === s.id
                      ? "border-[#bc6c25] bg-[#bc6c25]/5"
                      : "border-[#2c4a3b]/10 hover:border-[#2c4a3b]/30"
                  }`}
                >
                  <p className="text-sm font-semibold text-[#1a2421]">{s.label}</p>
                  <p className="text-xs text-[#5c6b61] mt-1">{s.hint}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Composer */}
          <div className="rounded-2xl border border-[#2c4a3b]/10 bg-white p-5">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#5c6b61] font-semibold mb-3">2. Escribe el correo</p>
            <Input
              data-testid="broadcast-subject"
              placeholder="Asunto del correo (usa {{name}} para personalizar)"
              value={subject}
              onChange={(e) => { setSubject(e.target.value); setDryRun(null); }}
              className="mb-3"
            />
            <Textarea
              data-testid="broadcast-body"
              placeholder="Cuerpo HTML. Puedes incluir <p>, <a href=''>, <strong>, etc. Y {{name}} para personalizar."
              value={body}
              onChange={(e) => { setBody(e.target.value); setDryRun(null); }}
              className="min-h-[280px] font-mono text-xs"
            />
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#5c6b61] mr-1 self-center">Plantillas:</span>
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  onClick={() => applyPreset(p)}
                  className="text-xs px-3 py-1.5 rounded-full bg-[#f0ebe1] hover:bg-[#bc6c25]/10 text-[#1a2421]"
                  data-testid={`preset-${p.label.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="rounded-2xl border border-[#2c4a3b]/10 bg-white p-5">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#5c6b61] font-semibold mb-3">3. Preview & envío</p>
            <div className="flex gap-2">
              <Button
                onClick={preview}
                variant="outline"
                className="border-[#2c4a3b]/20"
                data-testid="broadcast-preview-btn"
              >
                <Eye className="w-4 h-4 mr-2" /> Preview destinatarios
              </Button>
              <Button
                onClick={send}
                disabled={sending || !dryRun || (dryRun?.recipient_count || 0) === 0}
                data-testid="broadcast-send-btn"
                className="bg-[#bc6c25] hover:bg-[#9c581e] text-white"
              >
                <Send className="w-4 h-4 mr-2" />
                {sending ? "Enviando..." : "Enviar ahora"}
              </Button>
            </div>

            {dryRun && (
              <div className="mt-4 p-4 rounded-xl bg-[#bc6c25]/5 border border-[#bc6c25]/20">
                <p className="text-sm text-[#1a2421]">
                  <strong>{dryRun.recipient_count}</strong> destinatarios en segmento <em>{dryRun.segment}</em>.
                </p>
                {dryRun.sample_emails?.length > 0 && (
                  <p className="text-xs text-[#5c6b61] mt-1">
                    Ejemplos: {dryRun.sample_emails.join(", ")}
                  </p>
                )}
              </div>
            )}

            {lastResult && (
              <div className="mt-4 p-4 rounded-xl bg-[#2c4a3b]/5 border border-[#2c4a3b]/20">
                <div className="flex items-center gap-2 text-[#2c4a3b]">
                  <Check className="w-4 h-4" />
                  <p className="text-sm font-semibold">Broadcast enviado</p>
                </div>
                <p className="text-xs text-[#5c6b61] mt-1">
                  Total: {lastResult.total} · Enviados: {lastResult.sent} · Fallidos: {lastResult.failed_count}
                </p>
                {lastResult.failed_count > 0 && (
                  <div className="mt-2 flex items-start gap-2 text-xs text-[#bc6c25]">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
                    <span>Algunos correos fallaron. Revisa logs del backend para detalle.</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar preview */}
        <aside className="rounded-2xl border border-[#2c4a3b]/10 bg-white p-5 lg:sticky lg:top-6 self-start">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#5c6b61] font-semibold mb-3">Vista previa</p>
          <div className="rounded-xl border border-[#2c4a3b]/10 bg-[#f9f6f0] p-4">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#5c6b61]">Asunto</p>
            <p className="text-sm font-semibold text-[#1a2421] mt-1 break-words">
              {subject ? subject.replace(/\{\{name\}\}/g, "Sarai") : <em className="text-[#5c6b61]">(vacío)</em>}
            </p>
          </div>
          <div
            className="mt-3 prose-article max-h-[400px] overflow-y-auto text-sm"
            dangerouslySetInnerHTML={{
              __html: body
                ? body.replace(/\{\{name\}\}/g, "Sarai")
                : '<p class="text-[#5c6b61] italic">(cuerpo vacío)</p>',
            }}
          />
        </aside>
      </div>
    </AdminLayout>
  );
}
